import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from "@mui/icons-material/Home";
import ShieldIcon from "@mui/icons-material/Shield";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CarIcon from "@mui/icons-material/DirectionsCar";
import ExitIcon from "@mui/icons-material/ExitToApp";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import OfferIcon from "@mui/icons-material/LocalOffer";
import NotifyIcon from "@mui/icons-material/NotificationsActive";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import { api } from "common";
import { colors } from "../components/Theme/WebTheme";
import { useTranslation } from "react-i18next";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MoneyIcon from "@mui/icons-material/Money";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SosIcon from "@mui/icons-material/Sos";
import { FONT_FAMILY } from "../common/sharedFunctions";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import packageInfo from "../../package.json";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import InfoIcon from "@mui/icons-material/Info";
import TuneIcon from "@mui/icons-material/Tune";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import LanguageIcon from "@mui/icons-material/Language";
import MailIcon from "@mui/icons-material/Mail";
import SmsIcon from "@mui/icons-material/Sms";
import BlockIcon from "@mui/icons-material/Block";
import TimelineIcon from "@mui/icons-material/Timeline";
import CalculateIcon from "@mui/icons-material/Calculate";
import TelegramIcon from "@mui/icons-material/Telegram";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ContactsIcon from "@mui/icons-material/Contacts";
import PostAddIcon from "@mui/icons-material/PostAdd";
import Divider from "@mui/material/Divider";
import moment from "moment";

// ✅ [I18N_ROUTE] Single source of truth for /en prefix
import { toLocalizedPath } from "../utils/i18nRoute";
import { navigateWithIframeEscape } from "../utils/navigationUtils";
import { haptic } from "../utils/haptics";
import { useThemeMode } from "../contexts/ThemeModeContext";

const drawerWidth = 280; // Slightly wider for better legibility

export default function ResponsiveDrawer(props) {
  const { themeMode: contextThemeMode, darkMode: contextDarkMode, toggleDarkMode: contextToggleDarkMode } = useThemeMode();
  const themeMode = props.themeMode || contextThemeMode || "system";
  const darkMode = props.darkMode !== undefined ? props.darkMode : contextDarkMode;
  const toggleDarkMode = props.toggleDarkMode || contextToggleDarkMode;

  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { signOff, deleteUser, fetchUsersOnce } = api;
  const auth = useSelector((state) => state.auth);
  const staticusers = useSelector((state) => state.usersdata?.staticusers);
  const settings = useSelector((state) => state.settingsdata.settings);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // --- [I18N_ROUTE] Helpers ---
  const getCurrentLangPath = () =>
    location.pathname || window.location.pathname || "/";

  const localize = (path) => toLocalizedPath(path, getCurrentLangPath());

  const isActivePath = (targetUrl) => {
    const target = localize(targetUrl);
    const curr = getCurrentLangPath();

    // exact match
    if (curr === target) return true;

    // nested pages (ex: /en/bookings/bookingdetails/xxx)
    if (target !== "/" && target !== "/en" && curr.startsWith(target + "/"))
      return true;

    // home match
    if (
      targetUrl === "/" &&
      (curr === "/" || curr === "/en" || curr.startsWith("/en/"))
    )
      return true;

    return false;
  };

  const currentActiveLang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";

  const switchLanguage = (targetLang) => {
    try {
      if (currentActiveLang === targetLang) return;
      haptic.selection();

      const dateLocale = targetLang === "en" ? "en-gb" : "fr";
      i18n.changeLanguage(targetLang);
      moment.locale(dateLocale);
      localStorage.setItem("lang", JSON.stringify({ langLocale: targetLang, dateLocale }));

      // Navigate to localized route for current page
      const currentPath = location.pathname + location.search + location.hash;
      const targetPath = toLocalizedPath(currentPath, targetLang);
      navigateWithIframeEscape(targetPath, navigate);
    } catch (e) {
      console.error("[ResponsiveDrawer] Language switch error:", e);
    }
  };

  const [openLangModal, setOpenLangModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const userTypeLower = (auth?.profile?.usertype || role || "").toLowerCase();
  const isSuperAdmin = userTypeLower === "admin";

  useEffect(() => {
    if (isSuperAdmin && (!staticusers || staticusers.length === 0)) {
      if (typeof fetchUsersOnce === "function") {
        dispatch(fetchUsersOnce());
      }
    }
  }, [isSuperAdmin, staticusers, dispatch, fetchUsersOnce]);

  const activeSuperAdminsCount = React.useMemo(() => {
    if (!staticusers || !Array.isArray(staticusers)) return null;
    return staticusers.filter(
      (u) =>
        u.usertype === "admin" &&
        u.isActive !== false &&
        !u.accountDeleted &&
        !u.isDeleted
    ).length;
  }, [staticusers]);

  // Le bouton est actif UNIQUEMENT si dans la base on a minimum 2 admins (les fleetadmins ne comptent pas)
  const isDeleteDisabled = isSuperAdmin && (activeSuperAdminsCount === null || activeSuperAdminsCount < 2);

  const adminHelperText =
    currentActiveLang === "en"
      ? "Deletion disabled: There must be at least two active administrators in the system to delete an administrator account (fleet managers do not count)."
      : "Suppression impossible : Il doit y avoir au minimum deux administrateurs actifs dans le système pour pouvoir supprimer un administrateur (les gestionnaires de flotte ne comptent pas).";

  const LogOut = () => {
    dispatch(signOff());
    navigateWithIframeEscape("/", navigate);
  };

  const handleDeleteAccount = async () => {
    if (isDeleteDisabled) {
      setDeleteError(adminHelperText);
      return;
    }
    setDeletingAccount(true);
    setDeleteError(null);
    try {
      const uid = auth?.profile?.uid || auth?.currentUser?.uid;
      const res = await dispatch(deleteUser(uid));
      if (res && !res.success) {
        if (res.error === "LAST_ADMIN_CANNOT_BE_DELETED") {
          setDeleteError(t("delete_admin_error_last_admin", "Impossible de supprimer cet administrateur. C'est le dernier administrateur actif du système. Veuillez d'abord créer un autre administrateur."));
        } else {
          setDeleteError(res.message || res.error || t("delete_account_failed", "Échec de la suppression du compte."));
        }
        setDeletingAccount(false);
        return;
      }
      setOpenDeleteModal(false);
      setDeletingAccount(false);
      navigateWithIframeEscape("/", navigate);
    } catch (err) {
      console.error("[ResponsiveDrawer] Delete account error:", err);
      setDeleteError(err?.message || "Une erreur est survenue lors de la suppression.");
      setDeletingAccount(false);
    }
  };

  const handleHomeClick = () => {
    haptic.light();
    handleDrawerClose();
    navigateWithIframeEscape(localize("/"), navigate);
  };

  const handleItemClick = (item) => {
    haptic.light();
    handleDrawerClose();
    if (item.url === "logout") {
      LogOut();
      return;
    }
    if (item.url === "delete_account") {
      if (isDeleteDisabled) {
        return;
      }
      setDeleteError(null);
      setOpenDeleteModal(true);
      return;
    }
    if (item.url === "language_dialog") {
      setOpenLangModal(true);
      return;
    }
    const isAuthValid = Boolean(
      auth.profile &&
      auth.profile.uid &&
      !auth.profile.accountDeleted &&
      !auth.profile.isDeleted &&
      !auth.accountDeleted &&
      !auth.isDeleted &&
      auth.error?.msg !== 'ACCOUNT_DELETED'
    );
    if (!isAuthValid) {
      dispatch(signOff());
      navigateWithIframeEscape(localize("/login"), navigate);
      return;
    }
    const target = localize(item.url);
    navigateWithIframeEscape(target, navigate);
  };

  const [role, setRole] = useState(null);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  useEffect(() => {
    const isDeleted = Boolean(
      auth.error?.msg === 'ACCOUNT_DELETED' ||
      auth.accountDeleted ||
      auth.isDeleted ||
      (auth.profile && (auth.profile.accountDeleted === true || auth.profile.isDeleted === true))
    );
    if (isDeleted) {
      dispatch(signOff());
      navigateWithIframeEscape(localize("/login"), navigate);
    }
  }, [auth.error?.msg, auth.accountDeleted, auth.isDeleted, auth.profile?.accountDeleted, auth.profile?.isDeleted, dispatch, signOff, navigate]);

  const handleDrawerToggle = () => {
    haptic.light();
    setMobileOpen(!mobileOpen);
  };
  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  // --- Menu Structure Definition ---
  const menuStructure = [
    {
      header: t("main_menu", "Main"),
      items: [
        {
          name: t("home"),
          url: "/",
          icon: <HomeIcon />,
          access: ["admin", "fleetadmin", "driver", "customer"],
        },
        {
          name: t("dashboard_text"),
          url: "/dashboard",
          icon: <DashboardIcon />,
          access: ["admin", "fleetadmin"],
        },
        {
          name: t("overview", "Overview"),
          url: "/overview",
          icon: <DashboardIcon />,
          access: ["customer", "driver"],
        },
      ],
    },
    {
      header: t("operations", "Operations"),
      items: [
        {
          name: t("booking_history"),
          url: "/bookings",
          icon: <ViewListIcon />,
          access: ["admin", "fleetadmin", "driver", "customer"],
        },
        {
          name: t("addbookinglable"),
          url: "/addbookings",
          icon: <ContactPhoneIcon />,
          access:
            settings && settings.appCat === "bidcab"
              ? ["customer"]
              : ["admin", "fleetadmin", "customer", "driver"],
        },
        {
          name: t("zone_manager", "Zone Manager"),
          url: "/zonemanager",
          icon: <AddLocationAltIcon />,
          access: ["admin"],
        },
        {
          name: t("zone_intelligence", "Zone Intelligence"),
          url: "/zone-intelligence",
          icon: <TimelineIcon />,
          access: ["admin"],
        },
        {
          name: t("reliability_loops", "Reliability & Loops"),
          url: "/reliability",
          icon: <ShieldIcon />,
          access: ["admin"],
        },
        {
          name: t("price_simulator", "Price Simulator"),
          url: "/admin/simulator",
          icon: <CalculateIcon />,
          access: ["admin"],
        },
      ],
    },
    {
      header: t("people", "People"),
      items: [
        {
          name: t("riders"),
          url: "/admin/clients",
          icon: <EmojiPeopleIcon />,
          access: ["admin", "fleetadmin"],
        },
        {
          name: t("customer_registry_menu", "Répertoire Clients"),
          url: "/customers",
          icon: <ContactsIcon />,
          access: ["admin", "fleetadmin"],
        },
        {
          name: t("drivers"),
          url: "/admin/chauffeurs",
          icon: <PersonIcon />,
          access: ["admin", "fleetadmin"],
        },
        {
          name: t("fleetadmins"),
          url: "/admin/gestionnaires",
          icon: <SupervisorAccountIcon />,
          access: ["admin"],
        },
        {
          name: t("alladmin"),
          url: "/admin/administrateurs",
          icon: <AdminPanelSettingsIcon />,
          access: ["admin"],
        },
      ],
    },
    {
      header: t("fleet", "Fleet"),
      items: [
        {
          name: t("cars"),
          url: "/cars",
          icon: <CarIcon />,
          access: ["admin", "fleetadmin", "driver"],
        },
        {
          name: t("combined_edition"),
          url: "/cartypes/global-pricing",
          icon: <CalculateIcon />,
          access: ["admin"],
        },
        {
          name: t("car_type"),
          url: "/cartypes",
          icon: <CarIcon />,
          access: ["admin"],
        },
      ],
    },
    {
      header: t("finance", "Finance"),
      items: [
        {
          name: t("my_wallet_tile"),
          url: "/userwallet",
          icon: <AccountBalanceWalletIcon />,
          access: ["driver", "customer", "fleetadmin"],
        },
        {
          name: t("billing_documents_menu", "Factures & avoirs"),
          url: "/userinvoices",
          icon: <ReceiptIcon />,
          access: ["admin", "customer"],
        },
        {
          name: t("create_document_menu", "Nouveau document"),
          url: "/create-document",
          icon: <PostAddIcon />,
          access: ["admin", "fleetadmin"],
        },
        {
          name: t("earnings", "Mes Revenus"),
          url: "/driverearnings",
          icon: <MoneyIcon />,
          access: ["driver"],
        },
        {
          name: t("add_to_wallet"),
          url: "/addtowallet",
          icon: <AccountBalanceWalletIcon />,
          access: ["admin"],
        },
        {
          name: t("withdraws_web"),
          url: "/withdraws",
          icon: <MoneyIcon />,
          access: ["admin"],
        },
        {
          name: t("report"),
          url: "/allreports",
          icon: <AssessmentIcon />,
          access: ["admin", "fleetadmin"],
        },
      ],
    },
    {
      header: t("settings_title", "Settings"),
      items: [
        {
          name: t("promos"),
          url: "/promos",
          icon: <OfferIcon />,
          access: ["admin"],
        },
        {
          name: t("push_notifications"),
          url: "/notifications",
          icon: <NotifyIcon />,
          access: ["admin"],
        },
        {
          name: t("app_info"),
          url: "/settings/app-info",
          icon: <InfoIcon />,
          access: ["admin"],
        },
        {
          name: t("general_settings"),
          url: "/settings/general",
          icon: <TuneIcon />,
          access: ["admin"],
        },
        {
          name: t("availability_vacation_title", "Vacances & Disponibilités"),
          url: "/settings/vacation",
          icon: <BeachAccessIcon />,
          access: ["admin"],
        },
        {
          name: t("surcharge_settings_title", "Majorations tarifaires"),
          url: "/settings/surcharges",
          icon: <TrendingUpIcon />,
          access: ["admin"],
        },
        {
          name: t("language"),
          url: "/settings/language",
          icon: <LanguageIcon />,
          access: ["admin"],
        },
        {
          name: t("smtp"),
          url: "/settings/smtp",
          icon: <MailIcon />,
          access: ["admin"],
        },
        {
          name: t("smssettings"),
          url: "/settings/sms",
          icon: <SmsIcon />,
          access: ["admin"],
        },
        {
          name: t("google_keys"),
          url: "/settings/google-keys",
          icon: <LanguageIcon />,
          access: ["admin"],
        },
        {
          name: t("cancellation_reasons_title"),
          url: "/settings/cancellation-reasons",
          icon: <BlockIcon />,
          access: ["admin"],
        },
        {
          name: t("telegram", "Telegram"),
          url: "/settings/telegram",
          icon: <TelegramIcon />,
          access: ["admin"],
        },
        {
          name: t("google_calendar_integration", "Google Calendar"),
          url: "/gcal-settings",
          icon: <LanguageIcon />,
          access: ["admin"],
        },
        { name: t("sos"), url: "/sos", icon: <SosIcon />, access: ["admin"] },
        {
          name: t("complain"),
          url: "/complain",
          icon: <AnnouncementOutlinedIcon />,
          access: ["admin"],
        },
        {
          name: t("payment_settings"),
          url: "/paymentsettings",
          icon: <PhoneIphoneIcon />,
          access: ["admin"],
        },
      ],
    },
    {
      header: t("account", "Account"),
      items: [
        {
          name: t("profile"),
          url: "/profile",
          icon: <AccountCircleIcon />,
          access: ["admin", "fleetadmin", "driver", "customer"],
        },
        {
          name: t("language", "Langue"),
          url: "language_dialog",
          icon: <LanguageIcon />,
          access: ["admin", "fleetadmin", "driver", "customer"],
        },
        {
          name: t("settings_title", "Settings"),
          url: "/settings",
          icon: <TuneIcon />,
          access: ["driver"],
        },
        {
          name: t("logout"),
          url: "logout",
          icon: <ExitIcon />,
          access: ["admin", "fleetadmin", "driver", "customer"],
        },
        {
          name: t("delete_account_lebel", "Supprimer le compte"),
          url: "delete_account",
          icon: <DeleteForeverIcon />,
          access: ["admin", "fleetadmin", "driver", "customer"],
          isDanger: true,
        },
      ],
    },
  ];

  // State to manage open/close of groups
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    // Determine which group should be open based on current localized route
    const currentPath = getCurrentLangPath();
    const newOpenGroups = {};

    menuStructure.forEach((group) => {
      const hasActiveItem = group.items.some((item) => {
        if (item.url === "logout" || item.url === "delete_account" || item.url === "language_dialog") return false;
        const target = localize(item.url);
        return currentPath === target || currentPath.startsWith(target + "/");
      });

      // Keep Main open + active group open
      if (hasActiveItem || group.header === t("main_menu", "Main")) {
        newOpenGroups[group.header] = true;
      }
    });

    setOpenGroups(newOpenGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, t]); // keep synced when route changes

  const handleGroupClick = (header) => {
    haptic.selection();
    setOpenGroups((prev) => ({ ...prev, [header]: !prev[header] }));
  };

  const drawer = (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.Sidebar_Background,
        color: colors.Sidebar_Content,
      }}
    >
      {/* 1. Logo Section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          width: "100%",
          padding: "32px 0",
          background: darkMode ? "rgba(0, 0, 0, 0.1)" : "rgba(0, 0, 0, 0.03)",
          marginBottom: "8px",
        }}
      >
        <Box
          component="button"
          onClick={handleHomeClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            outline: "none",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease",
            "&:hover": {
              transform: "scale(1.05)",
              opacity: 0.9,
            },
            "&:active": {
              transform: "scale(0.97)",
            },
            "&:focus-visible": {
              outline: "2px solid #E5A93C",
              borderRadius: "8px",
            },
          }}
          title={t("home", "Accueil")}
          aria-label={t("home", "Accueil")}
        >
          <img
            style={{
              maxWidth: "160px",
              height: "auto",
              maxHeight: "80px",
              borderRadius: "0",
              boxShadow: "none",
              pointerEvents: "none",
            }}
            src={darkMode ? require("../assets/img/logo138x75white.png") : require("../assets/img/logo138x75black.png")}
            alt="Logo"
          />
        </Box>
        <IconButton
          onClick={toggleDarkMode}
          title={
            themeMode === "dark"
              ? (currentActiveLang === "fr" ? "Mode Sombre (🌙) — Cliquer pour Système (🖥️)" : "Dark Mode (🌙) — Click for System (🖥️)")
              : themeMode === "light"
                ? (currentActiveLang === "fr" ? "Mode Clair (☀️) — Cliquer pour Sombre (🌙)" : "Light Mode (☀️) — Click for Dark (🌙)")
                : (currentActiveLang === "fr" ? "Mode Système (🖥️) — Cliquer pour Clair (☀️)" : "System Mode (🖥️) — Click for Light (☀️)")
          }
          aria-label={
            themeMode === "dark"
              ? "Mode Sombre"
              : themeMode === "light"
                ? "Mode Clair"
                : "Mode Système"
          }
          sx={{
            position: 'absolute',
            right: 15,
            width: 44,
            height: 44,
            zIndex: 10,
            color: darkMode ? "#d4af37" : "#475569",
            backgroundColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
            boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.4)' : 'none',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)',
              transform: 'scale(1.1) rotate(15deg)',
              boxShadow: darkMode ? '0 6px 16px rgba(0,0,0,0.6)' : '0 2px 8px rgba(0,0,0,0.1)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {themeMode === "dark" ? (
            <DarkModeIcon sx={{ fontSize: 24 }} />
          ) : themeMode === "light" ? (
            <LightModeIcon sx={{ fontSize: 24 }} />
          ) : (
            <DesktopWindowsIcon sx={{ fontSize: 24 }} />
          )}
        </IconButton>
      </div>

      <Box sx={{ overflowY: "auto", flexGrow: 1, px: 2, pb: 2 }}>
        {/* Quick Language Flag Switcher */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            p: 0.5,
            mb: 1.5,
            borderRadius: "10px",
            background: darkMode
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.04)",
            border: `1px solid ${
              darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"
            }`,
          }}
        >
          {/* French Button */}
          <Box
            onClick={() => switchLanguage("fr")}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              py: 0.6,
              px: 1,
              borderRadius: "8px",
              cursor: "pointer",
              userSelect: "none",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                currentActiveLang === "fr"
                  ? "#C9A634"
                  : "transparent",
              color:
                currentActiveLang === "fr"
                  ? "#000000"
                  : (darkMode ? "rgba(255,255,255,0.85)" : "rgba(15,23,42,0.85)"),
              boxShadow:
                currentActiveLang === "fr"
                  ? "0 2px 6px rgba(201,166,52,0.4)"
                  : "none",
              "&:hover": {
                background:
                  currentActiveLang === "fr"
                    ? "#d4af37"
                    : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                color:
                  currentActiveLang === "fr"
                    ? "#000000"
                    : (darkMode ? "#FFFFFF" : "#000000"),
                transform: "scale(1.02)",
              },
            }}
          >
            <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>🇫🇷</span>
            <Typography
              variant="caption"
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: currentActiveLang === "fr" ? 800 : 700,
                fontSize: "0.78rem",
                letterSpacing: "0.5px",
              }}
            >
              Français
            </Typography>
          </Box>

          {/* English Button */}
          <Box
            onClick={() => switchLanguage("en")}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              py: 0.6,
              px: 1,
              borderRadius: "8px",
              cursor: "pointer",
              userSelect: "none",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                currentActiveLang === "en"
                  ? "#C9A634"
                  : "transparent",
              color:
                currentActiveLang === "en"
                  ? "#000000"
                  : (darkMode ? "rgba(255,255,255,0.85)" : "rgba(15,23,42,0.85)"),
              boxShadow:
                currentActiveLang === "en"
                  ? "0 2px 6px rgba(201,166,52,0.4)"
                  : "none",
              "&:hover": {
                background:
                  currentActiveLang === "en"
                    ? "#d4af37"
                    : (darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                color:
                  currentActiveLang === "en"
                    ? "#000000"
                    : (darkMode ? "#FFFFFF" : "#000000"),
                transform: "scale(1.02)",
              },
            }}
          >
            <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>🇬🇧</span>
            <Typography
              variant="caption"
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: currentActiveLang === "en" ? 800 : 600,
                fontSize: "0.78rem",
                letterSpacing: "0.5px",
              }}
            >
              English
            </Typography>
          </Box>
        </Box>

        {role ? (
          <List disablePadding>
            {menuStructure.map((group, groupIndex) => {
              const visibleItems = group.items.filter((item) =>
                item.access.includes(role),
              );
              if (visibleItems.length === 0) return null;

              return (
                <Box key={groupIndex} sx={{ mb: 1 }}>
                  {/* Group Header */}
                  {group.header !== t("main_menu", "Main") &&
                    group.header !== t("account", "Account") ? (
                    <ListItemButton
                      onClick={() => handleGroupClick(group.header)}
                      sx={{ py: 0.5, px: 1, minHeight: 32 }}
                    >
                      <ListItemText
                        primary={group.header}
                        primaryTypographyProps={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 50%)`,
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          fontFamily: FONT_FAMILY,
                        }}
                      />
                      {openGroups[group.header] ? (
                        <ExpandLess
                          sx={{
                            fontSize: 16,
                            color: `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 50%)`,
                          }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{
                            fontSize: 16,
                            color: `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 50%)`,
                          }}
                        />
                      )}
                    </ListItemButton>
                  ) : (
                    <Box sx={{ height: 8 }} />
                  )}

                  <Collapse
                    in={
                      group.header === t("main_menu", "Main") ||
                      group.header === t("account", "Account") ||
                      openGroups[group.header]
                    }
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {visibleItems.map((item, index) => {
                        const isActive = isActivePath(item.url);
                        const isDeleteItem = item.url === "delete_account";
                        const isItemDisabled = isDeleteItem && isDeleteDisabled;

                        const buttonNode = (
                          <ListItemButton
                            onClick={() => !isItemDisabled && handleItemClick(item)}
                            selected={isActive}
                            disabled={isItemDisabled}
                            sx={{
                              minHeight: 44,
                              justifyContent: "initial",
                              px: 2,
                              borderRadius: "12px",
                              transition: "all 0.2s",
                              opacity: isItemDisabled ? 0.45 : 1,
                              cursor: isItemDisabled ? "not-allowed !important" : "pointer",
                              backgroundColor: isActive
                                ? `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 80%)`
                                : "transparent",
                              color: item.isDanger
                                ? (isItemDisabled ? (darkMode ? "rgba(248, 113, 113, 0.4)" : "rgba(220, 38, 38, 0.4)") : (darkMode ? "#f87171" : "#dc2626"))
                                : colors.Sidebar_Content,
                              backdropFilter: isActive
                                ? "blur(10px)"
                                : "none",
                              boxShadow: isActive
                                ? darkMode ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.05)"
                                : "none",
                              "&:hover": {
                                backgroundColor: isItemDisabled
                                  ? "transparent"
                                  : item.isDanger
                                    ? (darkMode ? "rgba(248, 113, 113, 0.12)" : "rgba(220, 38, 38, 0.08)")
                                    : `color-mix(in srgb, ${colors.Sidebar_Content}, transparent ${darkMode ? 90 : 95}%)`,
                                transform: isItemDisabled ? "none" : "translateX(4px)",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: isRTL === "rtl" ? 0 : 2,
                                ml: isRTL === "rtl" ? 2 : 0,
                                justifyContent: "center",
                                color: item.isDanger
                                  ? (isItemDisabled ? (darkMode ? "rgba(248, 113, 113, 0.4)" : "rgba(220, 38, 38, 0.4)") : (darkMode ? "#f87171" : "#dc2626"))
                                  : isActive
                                    ? colors.Sidebar_Content
                                    : `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 30%)`,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                fontFamily: FONT_FAMILY,
                                fontWeight: item.isDanger ? 600 : (isActive ? 600 : 400),
                                fontSize: "0.9rem",
                                color: item.isDanger
                                  ? (isItemDisabled ? (darkMode ? "rgba(248, 113, 113, 0.4)" : "rgba(220, 38, 38, 0.4)") : (darkMode ? "#f87171" : "#dc2626"))
                                  : (isActive
                                    ? colors.Sidebar_Content
                                    : `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 20%)`),
                              }}
                              sx={{ opacity: 1 }}
                            />
                          </ListItemButton>
                        );

                        return (
                          <ListItem
                            key={index}
                            disablePadding
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {isItemDisabled ? (
                              <Tooltip
                                title={adminHelperText}
                                arrow
                                placement="right"
                              >
                                <span style={{ display: "block", width: "100%", cursor: "not-allowed" }}>
                                  {buttonNode}
                                </span>
                              </Tooltip>
                            ) : (
                              buttonNode
                            )}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>

                  {groupIndex < menuStructure.length - 1 && (
                    <Divider
                      sx={{ my: 1, borderColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }}
                    />
                  )}
                </Box>
              );
            })}
          </List>
        ) : null}
      </Box>

      {/* Footer Version Info */}
      <Typography
        sx={{
          textAlign: "center",
          fontSize: "10px",
          color: `color-mix(in srgb, ${colors.Sidebar_Content}, transparent 70%)`,
          mb: 1,
          fontFamily: FONT_FAMILY,
        }}
      >
        {t("version_label", { version: packageInfo?.version || "1.6.0" })}
      </Typography>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex", direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
      <CssBaseline />
      <style>
        {`
          /* Standard browser/WebKit suppression */
          ::-webkit-scrollbar {
            width: 0px !important;
            height: 0px !important;
            background: transparent !important;
            display: none !important;
          }
          ::-webkit-scrollbar-thumb {
            background: transparent !important;
            width: 0px !important;
          }
          ::-webkit-scrollbar-track {
            background: transparent !important;
            width: 0px !important;
          }
          
          /* Firefox & Legacy Edge suppression */
          html, body, *, #root {
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            scrollbar-color: transparent transparent !important;
          }
        `}
      </style>

      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          paddingTop: "env(safe-area-inset-top, 0px)",
          background: `color-mix(in srgb, ${colors.Header_Background || "#fff"
            }, transparent 2%)`, // More opaque (98%)
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid color-mix(in srgb, ${colors.BLACK
            }, transparent 95%)`,
          color: colors.Header_Content,
          ...(isRTL === "rtl"
            ? { marginRight: { sm: `${drawerWidth}px` }, marginLeft: 0 }
            : {}),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              noWrap
              component="div"
              onClick={handleHomeClick}
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                color: "inherit",
                cursor: "pointer",
                userSelect: "none",
                transition: "opacity 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  opacity: 0.85,
                  transform: "scale(1.02)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
              title={t("home", "Accueil")}
            >
              {settings && settings.appName ? settings.appName : ""}
            </Typography>

            {/* Quick Language Toggle in Top AppBar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, sm: 0.5 },
                bgcolor: darkMode ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.05)",
                p: "3px",
                borderRadius: "24px",
                border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)"}`,
                backdropFilter: "blur(8px)",
                boxShadow: darkMode ? "inset 0 1px 2px rgba(0, 0, 0, 0.4)" : "inset 0 1px 2px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Box
                onClick={() => switchLanguage("fr")}
                sx={{
                  px: { xs: 1.4, sm: 1.2 },
                  py: { xs: 0.6, sm: 0.4 },
                  minHeight: { xs: "34px", sm: "30px" },
                  borderRadius: "18px",
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 900,
                  bgcolor: currentActiveLang === "fr" ? "#E5A93C" : "transparent",
                  color: currentActiveLang === "fr" ? "#000000 !important" : (darkMode ? "#FFFFFF !important" : "#0F172A !important"),
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  boxShadow: currentActiveLang === "fr" ? "0 2px 6px rgba(0,0,0,0.25)" : "none",
                  transform: currentActiveLang === "fr" ? "scale(1.02)" : "scale(1)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: currentActiveLang === "fr" ? "#d4af37" : (darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)"),
                  },
                }}
              >
                <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>🇫🇷</span>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 900,
                    fontSize: { xs: "0.82rem", sm: "0.78rem" },
                    letterSpacing: "0.5px",
                    color: currentActiveLang === "fr" ? "#000000 !important" : (darkMode ? "#FFFFFF !important" : "#0F172A !important"),
                  }}
                >
                  FR
                </Typography>
              </Box>

              <Box
                onClick={() => switchLanguage("en")}
                sx={{
                  px: { xs: 1.4, sm: 1.2 },
                  py: { xs: 0.6, sm: 0.4 },
                  minHeight: { xs: "34px", sm: "30px" },
                  borderRadius: "18px",
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 900,
                  bgcolor: currentActiveLang === "en" ? "#E5A93C" : "transparent",
                  color: currentActiveLang === "en" ? "#000000 !important" : (darkMode ? "#FFFFFF !important" : "#0F172A !important"),
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  boxShadow: currentActiveLang === "en" ? "0 2px 6px rgba(0,0,0,0.25)" : "none",
                  transform: currentActiveLang === "en" ? "scale(1.02)" : "scale(1)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: currentActiveLang === "en" ? "#d4af37" : (darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)"),
                  },
                }}
              >
                <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>🇬🇧</span>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 900,
                    fontSize: { xs: "0.82rem", sm: "0.78rem" },
                    letterSpacing: "0.5px",
                    color: currentActiveLang === "en" ? "#000000 !important" : (darkMode ? "#FFFFFF !important" : "#0F172A !important"),
                  }}
                >
                  EN
                </Typography>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile Drawer */}
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          anchor={isRTL === "rtl" ? "right" : "left"}
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none",
              boxShadow: "4px 0 24px rgba(0,0,0,0.05)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: (location.pathname.includes("addbookings") || location.pathname.includes("dashboard"))
            ? 0
            : (location.pathname.includes("create-document") || location.pathname.includes("edit-document"))
              ? { xs: 0, sm: 3 }
              : { xs: 1, sm: 3 },
          py: (location.pathname.includes("addbookings") || location.pathname.includes("dashboard"))
            ? 0
            : (location.pathname.includes("create-document") || location.pathname.includes("edit-document"))
              ? { xs: 0, sm: 3 }
              : { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: (location.pathname.includes("addbookings") || location.pathname.includes("dashboard")) ? 'transparent' : colors.LandingPage_Background,
        }}
      >
        <Toolbar
          sx={{
            paddingTop: "env(safe-area-inset-top, 0px)",
            boxSizing: "content-box",
          }}
        />
        {props.children}
      </Box>

      {/* Language Selection Modal for all users */}
      <Dialog
        open={openLangModal}
        onClose={() => setOpenLangModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            bgcolor: darkMode ? "#1e293b" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#1e293b",
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 800, fontSize: "1.15rem", pb: 1 }}>
          🌐 {t("choose_language", "Choisir la langue")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
            {/* French Option */}
            <Paper
              elevation={0}
              onClick={() => {
                switchLanguage("fr");
                setOpenLangModal(false);
              }}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "12px",
                cursor: "pointer",
                border: `2px solid ${currentActiveLang === "fr" ? "#C9A634" : (darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)")}`,
                bgcolor: currentActiveLang === "fr" ? "rgba(201, 166, 52, 0.15)" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#C9A634",
                  transform: "translateY(-1px)",
                }
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <span style={{ fontSize: "1.6rem" }}>🇫🇷</span>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Français</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Langue française</Typography>
                </Box>
              </Box>
              {currentActiveLang === "fr" && (
                <Chip label={t("active", "Actif")} size="small" sx={{ bgcolor: "#C9A634", color: "#000", fontWeight: 800 }} />
              )}
            </Paper>

            {/* English Option */}
            <Paper
              elevation={0}
              onClick={() => {
                switchLanguage("en");
                setOpenLangModal(false);
              }}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "12px",
                cursor: "pointer",
                border: `2px solid ${currentActiveLang === "en" ? "#C9A634" : (darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)")}`,
                bgcolor: currentActiveLang === "en" ? "rgba(201, 166, 52, 0.15)" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#C9A634",
                  transform: "translateY(-1px)",
                }
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <span style={{ fontSize: "1.6rem" }}>🇬🇧</span>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>English</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>English language</Typography>
                </Box>
              </Box>
              {currentActiveLang === "en" && (
                <Chip label={t("active", "Actif")} size="small" sx={{ bgcolor: "#C9A634", color: "#000", fontWeight: 800 }} />
              )}
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Modal */}
      <Dialog
        open={openDeleteModal}
        onClose={() => !deletingAccount && setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1.5,
            bgcolor: darkMode ? "#1e293b" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#1e293b",
            boxShadow: darkMode
              ? "0 20px 40px rgba(0, 0, 0, 0.6)"
              : "0 20px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 2, pb: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: darkMode ? "rgba(239, 68, 68, 0.15)" : "rgba(220, 38, 38, 0.1)",
              color: darkMode ? "#f87171" : "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px auto",
            }}
          >
            <DeleteForeverIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 800,
              fontSize: "1.25rem",
              color: darkMode ? "#FFFFFF" : "#0F172A",
            }}
          >
            {t("delete_account_lebel", "Supprimer le compte")}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: "0.95rem",
              fontWeight: 600,
              color: darkMode ? "rgba(255,255,255,0.9)" : "#334155",
              textAlign: "center",
              mb: 1.5,
            }}
          >
            {t("delete_account_modal_subtitle", "Voulez-vous supprimer votre compte ?")}
          </Typography>

          <Box
            sx={{
              p: 1.5,
              borderRadius: "12px",
              bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: "0.82rem",
                color: darkMode ? "rgba(255,255,255,0.7)" : "#64748B",
                lineHeight: 1.5,
              }}
            >
              {currentActiveLang === "en"
                ? "This action will permanently revoke your access and disable your login. In accordance with legal and accounting requirements, past transport and invoice records remain securely preserved."
                : "Cette action révoquera immédiatement et définitivement votre accès et votre connexion. Conformément aux obligations légales et comptables, l'historique de vos factures et courses passées reste archivé de manière sécurisée."}
            </Typography>
          </Box>

          {isDeleteDisabled && (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                borderRadius: "10px",
                fontFamily: FONT_FAMILY,
                fontSize: "0.85rem",
                fontWeight: 600,
                alignItems: "center",
              }}
            >
              {adminHelperText}
            </Alert>
          )}

          {deleteError && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                borderRadius: "10px",
                fontFamily: FONT_FAMILY,
                fontSize: "0.85rem",
              }}
            >
              {deleteError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenDeleteModal(false)}
            disabled={deletingAccount}
            sx={{
              flex: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              borderColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
              color: darkMode ? "#FFFFFF" : "#334155",
              "&:hover": {
                borderColor: darkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
              },
            }}
          >
            {t("cancel", "Annuler")}
          </Button>

          <Tooltip
            title={isDeleteDisabled ? adminHelperText : ""}
            arrow
            placement="top"
          >
            <span style={{ display: "inline-flex", flex: 1.2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteAccount}
                disabled={deletingAccount || isDeleteDisabled}
                startIcon={
                  deletingAccount ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DeleteForeverIcon />
                  )
                }
                sx={{
                  width: "100%",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontFamily: FONT_FAMILY,
                  fontWeight: 700,
                  bgcolor: isDeleteDisabled
                    ? (darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)")
                    : "#dc2626",
                  color: isDeleteDisabled
                    ? (darkMode ? "rgba(255, 255, 255, 0.38)" : "rgba(0, 0, 0, 0.38)")
                    : "#FFFFFF",
                  cursor: isDeleteDisabled ? "not-allowed !important" : "pointer",
                  "&:hover": {
                    bgcolor: isDeleteDisabled
                      ? (darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)")
                      : "#b91c1c",
                  },
                }}
              >
                {deletingAccount
                  ? (currentActiveLang === "en" ? "Deleting..." : "Suppression...")
                  : t("delete_account_lebel", "Supprimer le compte")}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
